"use client";

/**
 * The listing mini-map, loaded only when it is about to be seen.
 *
 * MapLibre is ~1 MB of script and a WebGL context. The map sits at the
 * bottom of the listing panel (about 400 px below the fold on a laptop) and far down the listing pages, and most
 * readers never get there, yet every panel open paid for it (measured on
 * production 2026-10-13: 1 008 KB downloaded, ~6 MB of heap). Until the
 * placeholder comes within 100 px of being visible in its own scroll
 * container (the panel, or the page), only the placeholder renders.
 *
 * And not before the visitor's first move (a scroll, tap, key or pointer
 * move), as LazyMapMount does: on a laptop the listing page's map is in
 * the first screen, so being near the view alone still started the
 * library during load (Google's desktop run, 2026-10-27: ~1.8 s of main
 * thread, score 68-71). The first mouse move starts it, a moment after
 * the page settles.
 */
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import type { MapDot } from "./map-dots";

const ListingMiniMap = dynamic(() => import("./ListingMiniMap"), { ssr: false });

const INTENT_EVENTS = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart", "wheel"] as const;

interface Props {
  lat: number;
  lng: number;
  interactive?: boolean;
  dots?: MapDot[];
  tall?: boolean;
  zoom?: number;
}

/** The nearest ancestor that scrolls (the panel), else the viewport. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

export default function LazyMiniMap(props: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const [intent, setIntent] = useState(false);
  useEffect(() => {
    if (intent) return;
    const on = () => setIntent(true);
    // Capture: a scroll inside the listing panel does not bubble to window.
    INTENT_EVENTS.forEach((ev) => window.addEventListener(ev, on, { passive: true, once: true, capture: true }));
    return () => INTENT_EVENTS.forEach((ev) => window.removeEventListener(ev, on, { capture: true }));
  }, [intent]);
  useEffect(() => {
    const el = ref.current;
    if (!el || show) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { setShow(true); io.disconnect(); } },
      { root: scrollParent(el), rootMargin: "100px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);

  if (show && intent) return <ListingMiniMap {...props} />;
  const h = props.tall ? "h-72 sm:h-96" : props.interactive ? "h-64 sm:h-72" : "h-44";
  return <div ref={ref} className={`${h} rounded-xl border border-gray-200 bg-gray-50`} aria-hidden />;
}
